import { QueryHandlingService } from '@akebono/core';
import { Injectable } from '@angular/core';
import { iif, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { AlertsCountry } from 'src/app/const';

import {
  Alert,
  AlertFragment,
  AlertsGQL,
  AlertTargetEnum,
  UserCountryIsoGQL,
} from '../../graphql/service/graphql-auto-main-service';
import { AuctionService } from 'src/app/services/auction.service';

@Injectable()
export class AlertService {
  private readonly alertsCountry$: Observable<AlertsCountry>;

  constructor(
    private qhs: QueryHandlingService,
    private auctionService: AuctionService,
    private alertsGQL: AlertsGQL,
    private userCountryIsoGQL: UserCountryIsoGQL,
  ) {
    this.alertsCountry$ = this.qhs.fetch(this.userCountryIsoGQL).data.pipe(
      map((data) => this.countryIsoToAlertsCountry(data.currentUser?.countryIso)),
      shareReplay(1),
    );
  }

  public getAlerts(
    auctionName?: string,
    target?: AlertTargetEnum,
    lotCode?: string | number,
    isSanctioned?: boolean,
    company?: string,
    model?: string,
    frame?: string,
  ): Observable<Alert[]> {
    const auctionId$ = of(auctionName ?? null).pipe(
      switchMap((auctionName) =>
        iif(
          () => Boolean(auctionName),
          this.auctionService.getByName(auctionName).pipe(map((auction) => auction?.id)),
          of(null),
        ),
      ),
    );

    return auctionId$.pipe(
      switchMap((auctionId) =>
        this.alertsCountry$.pipe(map((alertsCountry) => ({ auctionId, alertsCountry }))),
      ),
      switchMap(({ auctionId, alertsCountry }) => {
        const variables: Record<string, any> = {
          target,
          auctionId,
          lotCode: Number(lotCode),
          isSanctioned,
          countryIso: alertsCountry,
        };

        if (this.hasValue(company)) {
          variables.catalogCompanyTitle = company;
        }

        if (this.hasValue(model)) {
          variables.catalogModelTitle = model;
        }

        if (this.hasValue(frame)) {
          variables.catalogFrameCode = frame;
        }

        return this.qhs.fetch(this.alertsGQL, variables, 'cache-first').data;
      }),
      map(({ globalAlerts, countryAlerts }) => [...(globalAlerts ?? []), ...(countryAlerts ?? [])]),
    );
  }

  public filterByCountryIso(
    alerts: AlertFragment[],
    userCountryIso: string | null,
  ): AlertFragment[] {
    const alertsCountry = this.countryIsoToAlertsCountry(userCountryIso);
    return alerts.filter(({ countryIso }) =>
      countryIso ? countryIso.toLowerCase() === alertsCountry : true,
    );
  }

  private countryIsoToAlertsCountry(countryIso: string | null): AlertsCountry {
    return countryIso?.toLowerCase() === 'ru' ? AlertsCountry.Ru : AlertsCountry.Another;
  }

  private hasValue(value?: string | null): value is string {
    return typeof value === 'string' && value.trim() !== '';
  }
}
